import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { Store } from '../Store';
import { getError } from '../utils';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Table from 'react-bootstrap/Table';
import Modal from 'react-bootstrap/Modal';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import QRCodeGenerator from '../components/QRCodeGenerator';

export default function TableManagement() {
    const { state } = useContext(Store);
    const { userInfo } = state;
    const [tables, setTables] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingTable, setEditingTable] = useState(null);

    // Form state
    const [tableNumber, setTableNumber] = useState('');
    const [roomNumber, setRoomNumber] = useState('');
    const [capacity, setCapacity] = useState(4);
    const [location, setLocation] = useState('Main Floor');
    const [status, setStatus] = useState('available');
    const [type, setType] = useState('table');

    const fetchTables = async () => {
        try {
            setLoading(true);
            const { data } = await axios.get('/api/tables', {
                headers: { Authorization: `Bearer ${userInfo.token}` },
            });
            setTables(data);
            setLoading(false);
        } catch (err) {
            toast.error(getError(err));
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTables();
    }, [userInfo]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const tableData = {
                tableNumber: type === 'table' ? tableNumber : '',
                roomNumber: type === 'room' ? roomNumber : '',
                capacity,
                location,
                status,
                type,
                restaurantId: userInfo.restaurantId
            };

            if (editingTable) {
                await axios.put(
                    `/api/tables/${editingTable._id}`,
                    tableData,
                    { headers: { Authorization: `Bearer ${userInfo.token}` } }
                );
                toast.success(`${type === 'room' ? 'Room' : 'Table'} updated successfully`);
            } else {
                await axios.post(
                    '/api/tables',
                    tableData,
                    { headers: { Authorization: `Bearer ${userInfo.token}` } }
                );
                toast.success(`${type === 'room' ? 'Room' : 'Table'} created successfully`);
            }
            setShowModal(false);
            fetchTables();
        } catch (err) {
            toast.error(getError(err));
        }
    };

    const deleteHandler = async (table) => {
        if (window.confirm('Are you sure you want to delete this table?')) {
            try {
                await axios.delete(`/api/tables/${table._id}`, {
                    headers: { Authorization: `Bearer ${userInfo.token}` },
                });
                toast.success('Table deleted successfully');
                fetchTables();
            } catch (err) {
                toast.error(getError(err));
            }
        }
    };

    const openModal = (table = null) => {
        if (table) {
            setEditingTable(table);
            setTableNumber(table.tableNumber || '');
            setRoomNumber(table.roomNumber || '');
            setCapacity(table.capacity);
            setLocation(table.location);
            setStatus(table.status);
            setType(table.type || 'table');
        } else {
            setEditingTable(null);
            setTableNumber('');
            setRoomNumber('');
            setCapacity(4);
            setLocation('Main Floor');
            setStatus('available');
            setType('table');
        }
        setShowModal(true);
    };

    const getMenuUrl = (table) => {
        // Encode both table ID (qrCodeId) and Restaurant ID
        // Note: We use table.restaurantId if available, otherwise fallback to userInfo.restaurantId if not set on table yet
        const busId = table.restaurantId || userInfo.restaurantId;
        const origin = process.env.REACT_APP_FRONTEND_URL || window.location.origin;
        return `${origin}/menu?table=${table.qrCodeId}&business=${busId || ''}&type=${table.type || 'table'}`;
    };

    return (
        <Container className="py-4">
            <Helmet>
                <title>Table&Room Management</title>
            </Helmet>

            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2>Point Management</h2>
                <Button onClick={() => openModal()} variant="primary">
                    <i className="fas fa-plus me-2"></i> Add {type === 'room' ? 'Room' : 'Table'}
                </Button>
            </div>

            {loading ? (
                <div>Loading tables...</div>
            ) : (
                <Row>
                    {tables.map((table) => (
                        <Col key={table._id} xs={12} md={6} lg={4} className="mb-4">
                            <QRCodeGenerator
                                value={getMenuUrl(table)}
                                tableNumber={table.type === 'room' ? table.roomNumber : table.tableNumber}
                                type={table.type || 'table'}
                                label={`${table.type === 'room' ? 'Room' : 'Table'} - ${table.location} (${table.capacity} seats)`}
                            />
                            <div className="mt-2 text-center">
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    className="me-2"
                                    onClick={() => openModal(table)}
                                >
                                    Edit
                                </Button>
                                <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => deleteHandler(table)}
                                >
                                    Delete
                                </Button>
                            </div>
                        </Col>
                    ))}
                </Row>
            )}

            <Modal show={showModal} onHide={() => setShowModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{editingTable ? `Edit ${type === 'room' ? 'Room' : 'Table'}` : `Add New ${type === 'room' ? 'Room' : 'Table'}`}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Point Type</Form.Label>
                            <Form.Select
                                value={type}
                                onChange={(e) => setType(e.target.value)}
                            >
                                <option value="table">Table</option>
                                <option value="room">Room</option>
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>{type === 'room' ? 'Room Number' : 'Table Number'}</Form.Label>
                            <Form.Control
                                type="text"
                                required
                                value={type === 'room' ? roomNumber : tableNumber}
                                onChange={(e) => type === 'room' ? setRoomNumber(e.target.value) : setTableNumber(e.target.value)}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Location</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="e.g. Main Floor, Patio, VIP"
                                value={location}
                                onChange={(e) => setLocation(e.target.value)}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Capacity (Seats)</Form.Label>
                            <Form.Control
                                type="number"
                                required
                                min="1"
                                value={capacity}
                                onChange={(e) => setCapacity(e.target.value)}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Status</Form.Label>
                            <Form.Select
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                            >
                                <option value="available">Available</option>
                                <option value="occupied">Occupied</option>
                                <option value="reserved">Reserved</option>
                            </Form.Select>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowModal(false)}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit">
                            Save {type === 'room' ? 'Room' : 'Table'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </Container>
    );
}
